import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as empApi from '../../../lib/api/employee';
import useActionPermission from '../../../hooks/useActionPermission';

// PersonalTabs.js 탭 순서와 라벨 그대로 - HrResumeVisibilityService.KNOWN_INFO_TYPES와 1:1 대응.
const TAB_LABELS = [
  { infoTyp: 'CAREER_SCHOOL', label: '학교' },
  { infoTyp: 'DEPART_HISTORY', label: '부서이동' },
  { infoTyp: 'PROMOTION_INF', label: '승진승급' },
  { infoTyp: 'RESUME_INF', label: '경력사항' },
  { infoTyp: 'FAMILY_INF', label: '가족정보' },
  { infoTyp: 'REWARD_PUNISHMENT', label: '상벌조항' },
  { infoTyp: 'RESULT', label: '성과관리' },
  { infoTyp: 'ETC', label: '기타사항' },
];

/**
 * 사원 이력(PersonalTabs) 항목별 공개 권한 - 관리자 전용 화면. bito 4단계(2026-10-20).
 * 체크 해제 = 비공개(본인+관리자만). 실제 차단은 백엔드(HrResumeService.getHrResume)가 한다 -
 * 이 화면은 설정값만 바꾼다.
 */
const HrResumeVisibilityContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  // HR_RESUME_VIS_ADMIN은 ActionPermissionService가 모르는 코드라 일반 사용자에게는 절대 부여될
  // 수 없다 - allowed는 사실상 "관리자인가"와 같다(useActionPermission 구현 참고: isAdmin이면
  // 무조건 allowed=true).
  const { allowed: isAdmin, loading: adminCheckLoading } = useActionPermission(
    'HR_RESUME_VIS_ADMIN',
  );

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    empApi
      .getHrResumeVisibility()
      .then((res) => {
        if (mounted) {
          setSettings(res.data.settings || {});
        }
      })
      .catch(() => {
        enqueueSnackbar('공개 권한 설정을 불러오지 못했습니다', { variant: 'error' });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (infoTyp) => (event) => {
    setSettings((prev) => ({ ...prev, [infoTyp]: event.target.checked }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await empApi.putHrResumeVisibility(settings);
      setSettings(res.data.settings || settings);
      enqueueSnackbar('저장되었습니다', { variant: 'success' });
    } catch (err) {
      const status = err?.response?.status;
      enqueueSnackbar(
        status === 403 ? '관리자만 변경할 수 있습니다' : '저장에 실패했습니다',
        { variant: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  if (adminCheckLoading || loading || !settings) {
    return null;
  }

  if (!isAdmin) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        관리자만 접근할 수 있는 화면입니다.
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Typography variant="h6" gutterBottom>
        사원 이력 항목별 공개 권한
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        체크를 해제하면 해당 이력 탭은 본인과 관리자만 볼 수 있습니다(예: 경력사항).
      </Typography>
      <FormGroup>
        {TAB_LABELS.map(({ infoTyp, label }) => (
          <FormControlLabel
            key={infoTyp}
            control={
              <Checkbox
                checked={Boolean(settings[infoTyp])}
                onChange={handleToggle(infoTyp)}
              />
            }
            label={`${label} - 공개`}
          />
        ))}
      </FormGroup>
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          저장
        </Button>
      </Box>
    </Paper>
  );
};

export default HrResumeVisibilityContainer;
